import React, { createContext, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'querystring';

import TwoFaService from 'services/TwoFaService';
import TwoFaModal from 'components/TwoFaModal/TwoFaModal';
import APIStates from 'enum/APIStates';
import { ENUM_TWO_FA } from 'enum/profile';
import useInput from 'hooks/useInput';
import useModal from 'hooks/useModal';


export const TwoFaContext = createContext();

export const TwoFaContextConsumer = TwoFaContext.Consumer;

export function TwoFaContextWithModal(props) {
  const {
    children,
    formDataForResend,
    formDataForSubmit,
    isPayment,
    resendTwoFaUrl,
    urlEncoded,
    user,
    validateTwoFaUrl,
    withCredentials,
  } = props;

  const [
    {
      data,
      error,
      codeResent,
      codeValidate,
      validationResponse,
      errorMsg,
    },
    dispatch,
  ] = useReducer(
    TwoFaService.handleTextStatus,
    TwoFaService.initialTextStatus
  );

  const [show, open, close] = useModal();
  const [value, onChange, resetValue] = useInput();
  const [submitCodeStatus, setSubmitCodeStatus] = useState(APIStates.INITIAL);

  const formDataSubmit = (dataInfo) => {
    switch (dataInfo) {
      case ENUM_TWO_FA.PAYMENT_SUBMIT:
        return {
          operationId: data.operationId,
          validationCode: value,
        };
      case ENUM_TWO_FA.SETUP_SUBMIT:
        return qs.stringify({
          operationId: data.response.operationId,
          action: ENUM_TWO_FA.VALIDATE,
          validationCode: value,
        });
      case ENUM_TWO_FA.CHANGE_EMAIL_SUBMIT:
        return qs.stringify({
          operationId: data.response.operationId,
          action: ENUM_TWO_FA.VALIDATE,
          validationCode: value,
          username: user.username,
        });
      case ENUM_TWO_FA.FORGOT_PASSWORD_SUBMIT:
        return qs.stringify({
          username: data.username,
          operationId: data.response.operationId,
          code: value,
        });
      case ENUM_TWO_FA.CHANGE_PASSWORD_SUBMIT:
        return qs.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
          newPasswordConfirm: data.confirmNewPassword,
          action: ENUM_TWO_FA.VALIDATE,
          operationId: data.response.operationId,
          code: value,
        });
      default:
        return undefined;
    }
  };

  const formDataResend = (dataInfo) => {
    switch (dataInfo) {
      case ENUM_TWO_FA.PAYMENT_RESEND_CODE:
        return { operationId: data.operationId };
      case ENUM_TWO_FA.SETUP_RESEND:
        return qs.stringify({
          operationId: data.response.operationId,
          action: ENUM_TWO_FA.RESEND_TWO_FA_CODE,
        });
      case ENUM_TWO_FA.FORGOT_PASSWORD_RESEND:
        return qs.stringify({
          operationId: data.response.operationId,
          username: data.username,
          authToken: data.authToken,
        });
      case ENUM_TWO_FA.CHANGE_PASSWORD_RESEND:
        return qs.stringify({
          operationId: data.response.operationId,
          username: user.username,
          authToken: data.accessHash,
        });
      default:
        return undefined;
    }
  };

  const headers = urlEncoded ? { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' } : {};

  const headerData = (dataInfo) => {
    switch (dataInfo) {
      case ENUM_TWO_FA.PAYMENT_RESEND_CODE:
      case ENUM_TWO_FA.PAYMENT_SUBMIT:
        return {
          ...headers,
          Username: data.username,
          'Access-Hash': data.accessHash,
        };
      default:
        return headers;
    }
  };

  const onSubmitTwoFa = () => {
    setSubmitCodeStatus(APIStates.PENDING);
    TwoFaService.validateCode(
      formDataSubmit(formDataForSubmit),
      validateTwoFaUrl,
      headerData(formDataForSubmit),
      withCredentials
    ).then((res) => {
      setSubmitCodeStatus(APIStates.INITIAL);
      if (
        ['FAILED', 'INVALID_CODE', 'INVALID_PHONE_NUMBER', 'OPERATION_NOT_FOUND'].includes(res.status) ||
        (res.data &&
          ['FAILED', 'INVALID_CODE', 'INVALID_PHONE_NUMBER', 'OPERATION_NOT_FOUND'].includes(res.data.status)) ||
        (res.errorCode && ['SOURCE_ACCOUNT_UNAVAILABLE'].includes(res.errorCode))
      ) {
        if (formDataForSubmit === ENUM_TWO_FA.CHANGE_PASSWORD_SUBMIT) {
          dispatch({ type: 'setError', errorMsg: res.data.status });
        } else if (res.errorCode && isPayment) {
          dispatch({ type: 'setPaymentError', errorMsg: res.errorCode });
        } else {
          dispatch({ type: 'setError', errorMsg: res.status });
        }
      } else {
        if (isPayment) {
          dispatch({ type: 'setSuccess' });
        } else {
          close();
        }
        data.onSendCode(res);
      }
    });
  };

  const onResendTwoFa = () => {
    TwoFaService.resendCode(
      formDataResend(formDataForResend),
      resendTwoFaUrl,
      headerData(formDataForResend),
      withCredentials
    ).then((res) => {
      if (['FAILED', 'INVALID_CODE', 'INVALID_PHONE_NUMBER'].includes(res.status)) {
        dispatch({ type: 'setError', errorMsg: res.status });
      } else {
        dispatch({ type: 'codeResend' });
      }
    });
  };

  const openModal = (payload) => {
    open();
    resetValue();
    dispatch({ type: 'openModal', payload });
  };

  return (
    <TwoFaContext.Provider value={{ openModal, onSubmitTwoFa }}>
      {children}
      {show && (
        <TwoFaModal
          closeModal={close}
          errorMsg={errorMsg}
          handleTwoCode={onChange}
          isCodeResent={codeResent}
          isErrorMsgVisible={error}
          isPayment={codeValidate}
          isTwoFaModalOpen={show}
          resendTwoFaCode={onResendTwoFa}
          responseForTwoFa={validationResponse}
          submitCodeStatus={submitCodeStatus}
          twoFaCode={value}
          validateTwoFaCode={onSubmitTwoFa}
        />
      )}
    </TwoFaContext.Provider>
  );
}

TwoFaContextWithModal.propTypes = {
  children: PropTypes.node,
  formDataForResend: PropTypes.string,
  formDataForSubmit: PropTypes.string,
  isPayment: PropTypes.bool,
  resendTwoFaUrl: PropTypes.string,
  urlEncoded: PropTypes.bool,
  user: PropTypes.object,
  validateTwoFaUrl: PropTypes.string,
  withCredentials: PropTypes.bool,
};

TwoFaContextWithModal.defaultProps = {
  children: null,
  formDataForResend: undefined,
  formDataForSubmit: undefined,
  isPayment: false,
  resendTwoFaUrl: undefined,
  urlEncoded: false,
  user: {},
  validateTwoFaUrl: undefined,
  withCredentials: false,
};

export default TwoFaContextWithModal;
